import { useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { Drawing, DrawingSet, DrawingStatus, WorkstreamType } from "@/types";
import { WORKSTREAM_TYPES } from "@/types";
import { Button, Pill, SelectField } from "@/components/ui";
import {
  setCode,
  drawingCode,
  workstreamTypeLabel,
  workstreamStatusToPill,
  drawingStatusLabel,
  displayStatusToPill,
} from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { NewDrawingSetDrawer } from "./NewDrawingSetDrawer";
import { EditDrawingDrawer } from "./EditDrawingDrawer";

const DRAWING_STATUSES: DrawingStatus[] = ["not_started", "in_progress", "with_client", "complete"];

/** A set still has drawing work while any drawing isn't complete. This is the
 *  EXACT rule the backend uses to 400 new-set creation (not `is_complete`, which
 *  also needs the applications approved) — so "Complete drawing set", which
 *  force-finishes the drawings, unblocks the next set immediately. */
function hasIncompleteDrawings(set: DrawingSet): boolean {
  return set.drawings.some((d) => d.status !== "complete");
}

/** A set's read-only state comes ONLY from the backend flags — never inferred
 *  from the drawings. A set is editable while it's the current set and not yet
 *  complete; past sets (is_current=false) and backend-complete sets
 *  (is_complete=true) are locked. A current set can have EVERY drawing complete
 *  and still be open — e.g. drawings done but applications not yet submitted
 *  (is_complete=false, header_status='ready_to_submit') — so completing the last
 *  drawing must not collapse or close the set. */
function isSetLocked(set: DrawingSet): boolean {
  return !set.is_current || set.is_complete;
}

/** Drawing Sets tab (Addendum 2 §4). Renders the nested drawing_sets[] from
 *  /detail (current-first), each as a set card listing its drawings grouped by
 *  type. Every status is derived by the backend — the tab only calls the thin
 *  endpoints and re-reads /detail via onChanged(); it never computes status. */
export function DrawingSetsTab({
  jobId,
  jobNumber,
  drawingSets,
  onChanged,
  onError,
  onNotice,
  onOpenTaskBoard,
}: {
  jobId: number;
  jobNumber: number;
  drawingSets: DrawingSet[];
  onChanged: () => void;
  onError: (message: string) => void;
  onNotice: (kind: "info" | "warn", message: string) => void;
  /** Switches the job detail view to the Tasks tab (the draw/submit board). */
  onOpenTaskBoard: () => void;
}) {
  const [showNew, setShowNew] = useState(false);
  const [editDrawing, setEditDrawing] = useState<{ drawing: Drawing; setNumber: number } | null>(null);
  const [busyDrawingId, setBusyDrawingId] = useState<number | null>(null);
  const [busySetId, setBusySetId] = useState<number | null>(null);

  const currentSet = drawingSets.find((s) => s.is_current) ?? null;
  // Gate "New drawing set" on the backend's real rule — incomplete drawings in
  // the current set — so completing/force-finishing it unblocks the next one.
  const newBlocked = Boolean(currentSet && hasIncompleteDrawings(currentSet));

  async function changeDrawingStatus(drawing: Drawing, next: DrawingStatus) {
    if (busyDrawingId != null || next === drawing.status) return;
    setBusyDrawingId(drawing.id);
    try {
      await jobsApi.setDrawingStatus(drawing.id, next);
      onNotice("info", `${drawingStatusLabel(drawing.status)} → ${drawingStatusLabel(next)}`);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not change the drawing status.");
    } finally {
      setBusyDrawingId(null);
    }
  }

  async function createBcTask(drawing: Drawing) {
    if (busyDrawingId != null) return;
    setBusyDrawingId(drawing.id);
    try {
      await jobsApi.createBcTask(drawing.id);
      onNotice("info", "Draw task created.");
      onChanged(); // /detail comes back with draw_task_id set → the button hides
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not create the task.");
    } finally {
      setBusyDrawingId(null);
    }
  }

  async function addDrawing(set: DrawingSet, type: WorkstreamType) {
    if (busySetId != null) return;
    setBusySetId(set.id);
    try {
      const created = await jobsApi.addDrawing(set.id, type);
      // A new drawing inherits its set's title (label) when the set has one, so
      // it isn't left "Untitled". add_drawing seeds an empty title; we write the
      // inherited value via update_drawing before re-reading /detail (one reload,
      // no flash). Sets without a label keep the drawing untitled, as before.
      const inherited = set.label?.trim();
      if (inherited && created?.id) {
        await jobsApi.updateDrawing(created.id, { title: inherited });
      }
      onNotice("info", `${workstreamTypeLabel(type)} drawing added.`);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not add the drawing.");
    } finally {
      setBusySetId(null);
    }
  }

  async function completeSet(set: DrawingSet) {
    if (busySetId != null) return;
    if (!window.confirm("Complete this drawing set? Any unfinished drawings are force-finished and their draw tasks closed (no submit tasks spawned).")) return;
    setBusySetId(set.id);
    try {
      const res = await jobsApi.completeDrawingSet(set.id);
      onNotice("info", `Drawing set completed${res.completed ? ` (${res.completed} finished)` : ""}.`);
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not complete the drawing set.");
    } finally {
      setBusySetId(null);
    }
  }

  return (
    <div className="job-drawings">
      <div className="job-drawings__head">
        <p className="job-drawer__muted">{drawingSets.length} drawing set{drawingSets.length === 1 ? "" : "s"}</p>
        <Button
          variant="primary"
          size="sm"
          disabled={newBlocked}
          title={newBlocked ? "Finish the current drawing set first" : undefined}
          onClick={() => setShowNew(true)}
        >
          New drawing set
        </Button>
      </div>

      {drawingSets.length === 0 ? (
        <p className="jobs-list__empty">No drawing sets yet. Create one to start a drawing workstream.</p>
      ) : (
        drawingSets.map((set) => (
          <SetCard
            key={set.id}
            set={set}
            jobNumber={jobNumber}
            busyDrawingId={busyDrawingId}
            busySet={busySetId === set.id}
            onEditDrawing={(drawing) => setEditDrawing({ drawing, setNumber: set.set_number })}
            onOpenTaskBoard={onOpenTaskBoard}
            onDrawingStatus={changeDrawingStatus}
            onCreateBcTask={createBcTask}
            onAddDrawing={(type) => addDrawing(set, type)}
            onCompleteSet={() => completeSet(set)}
          />
        ))
      )}

      {showNew ? (
        <NewDrawingSetDrawer
          jobId={jobId}
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            onNotice("info", "Drawing set created.");
            onChanged();
          }}
        />
      ) : null}

      {editDrawing ? (
        <EditDrawingDrawer
          drawing={editDrawing.drawing}
          jobNumber={jobNumber}
          setNumber={editDrawing.setNumber}
          onClose={() => setEditDrawing(null)}
          onSaved={() => {
            setEditDrawing(null);
            onNotice("info", "Drawing updated.");
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function SetCard({
  set,
  jobNumber,
  busyDrawingId,
  busySet,
  onEditDrawing,
  onOpenTaskBoard,
  onDrawingStatus,
  onCreateBcTask,
  onAddDrawing,
  onCompleteSet,
}: {
  set: DrawingSet;
  jobNumber: number;
  busyDrawingId: number | null;
  busySet: boolean;
  onEditDrawing: (drawing: Drawing) => void;
  onOpenTaskBoard: () => void;
  onDrawingStatus: (drawing: Drawing, next: DrawingStatus) => void;
  onCreateBcTask: (drawing: Drawing) => void;
  onAddDrawing: (type: WorkstreamType) => void;
  onCompleteSet: () => void;
}) {
  const header = workstreamStatusToPill(set.header_status);
  const rollups = [
    { tag: "Planning", status: set.planning_status },
    { tag: "BC", status: set.bc_status },
    { tag: "Drawings", status: set.other_status },
  ].filter((r) => r.status !== "not_applicable");

  // A locked set is read-only — no add/status/create-task/complete/edit. Covers
  // past sets and the current set once its drawings are all complete.
  const locked = isSetLocked(set);

  const headerContent = (
    <>
      <span className="job-set-card__code numeric">{setCode(jobNumber, set.set_number)}</span>
      {set.label ? <span className="job-set-card__label">{set.label}</span> : null}
      <Pill tone={header.tone}>{header.label}</Pill>
      {set.is_current && !locked ? <span className="job-set-card__badge job-set-card__badge--current">Current</span> : null}
      {set.is_current && locked ? <span className="job-set-card__badge job-set-card__badge--done">Completed</span> : null}
      {!set.is_current && set.closed_at > 0 ? (
        <span className="job-set-card__badge">Closed {formatDateShort(set.closed_at)}</span>
      ) : null}
    </>
  );

  return (
    <section className={`job-set-card${set.is_current ? " job-set-card--current" : ""}${locked ? " job-set-card--locked" : ""}`}>
      {/* The set header is display-only; drawings are edited individually by
          clicking their card (see below). */}
      <div className="job-set-card__head job-set-card__head--static">{headerContent}</div>

      {rollups.length > 0 ? (
        <div className="job-set-card__rollups">
          {rollups.map((r) => {
            const pill = workstreamStatusToPill(r.status);
            return (
              <span key={r.tag} className="ws-item">
                <span className="ws-item__tag">{r.tag}</span>
                <Pill tone={pill.tone}>{pill.label}</Pill>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="job-set-card__drawings">
        {WORKSTREAM_TYPES.map((type) => {
          const drawings = set.drawings.filter((d) => d.drawing_type === type).sort((a, b) => a.drawing_number - b.drawing_number);
          if (drawings.length === 0) return null;
          return (
            <div key={type} className="job-set-group">
              <p className="job-set-group__label">{workstreamTypeLabel(type)}</p>
              {drawings.map((d) => {
                const pill = displayStatusToPill(d.display_status);
                return (
                <div
                  key={d.id}
                  className={`job-drawing${locked ? "" : " job-drawing--clickable"}`}
                  onClick={locked ? undefined : () => onEditDrawing(d)}
                  title={locked ? undefined : "Edit drawing"}
                >
                  <div className="job-drawing__main">
                    <span className="job-drawing__code numeric">{drawingCode(jobNumber, set.set_number, d.drawing_number)}</span>
                    {d.title ? <span className="job-drawing__title">{d.title}</span> : <span className="job-drawing__title job-drawing__title--empty">Untitled</span>}
                    <Pill tone={pill.tone}>{pill.label}</Pill>
                  </div>
                  {/* Inline controls must not open the edit drawer. */}
                  {!locked ? (
                    <div className="job-drawing__controls" onClick={(e) => e.stopPropagation()}>
                      {d.drawing_type === "building_control" && !d.draw_task_id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyDrawingId === d.id}
                          title="Building control drawings don't get a draw task automatically"
                          onClick={() => onCreateBcTask(d)}
                        >
                          Create task
                        </Button>
                      ) : null}
                      <SelectField
                        label="Status"
                        className="job-drawing__status"
                        value={d.status}
                        disabled={busyDrawingId === d.id}
                        onChange={(e) => onDrawingStatus(d, e.target.value as DrawingStatus)}
                      >
                        {DRAWING_STATUSES.map((s) => (
                          <option key={s} value={s}>{drawingStatusLabel(s)}</option>
                        ))}
                      </SelectField>
                    </div>
                  ) : null}
                </div>
                );
              })}
            </div>
          );
        })}
        {set.drawings.length === 0 ? <p className="job-drawer__muted">No drawings in this set yet.</p> : null}
      </div>

      <div className="job-set-card__actions">
        <button type="button" className="job-invoice__link" onClick={onOpenTaskBoard}>Open task board ↗</button>
        {set.is_current && !locked ? (
          <>
            <span className="job-set-card__add">
              <span className="job-drawer__muted">Add drawing:</span>
              {WORKSTREAM_TYPES.map((t) => (
                <Button key={t} variant="ghost" size="sm" disabled={busySet} onClick={() => onAddDrawing(t)}>
                  + {workstreamTypeLabel(t)}
                </Button>
              ))}
            </span>
            {/* Manual close — available on any open current set (even once every
                drawing is complete), so it can be finished without creating a new
                set. */}
            <Button variant="secondary" size="sm" loading={busySet} onClick={onCompleteSet}>
              Complete drawing set
            </Button>
          </>
        ) : null}
      </div>
    </section>
  );
}
